import { Charge } from '../types';
import { formatCurrency, formatDate } from '../utils/formatters';

interface SelectableChargesListProps {
  charges: Charge[];
  selectedCharge: Charge | null;
  onChargeSelect: (charge: Charge) => void;
}

export const SelectableChargesList = ({ charges, selectedCharge, onChargeSelect }: SelectableChargesListProps) => {
  const getCategoryIcon = (category: string) => {
    if (category.includes('REVENUE')) {
      return '🏠';
    } else if (category.includes('WATER')) {
      return '💧';
    } else if (category.includes('UTILITY')) {
      return '💡';
    } else if (category.includes('MAINTENANCE')) {
      return '🔧';
    } else {
      return '💳';
    }
  };

  if (charges.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500">
        <div className="text-4xl mb-2">📋</div>
        <p>No charges found</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {charges.map((charge) => {
        const isSelected = selectedCharge?.id === charge.id;
        
        return (
          <div 
            key={charge.id} 
            className={`p-4 cursor-pointer transition-all duration-200 border-2 border-transparent ${
              isSelected 
                ? 'bg-blue-50 border-blue-500 shadow-md' 
                : 'hover:bg-gray-50 hover:border-gray-200 hover:shadow-sm'
            }`}
            onClick={() => onChargeSelect(charge)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onChargeSelect(charge);
              }
            }}
            aria-label={`Select ${charge.chargeName} for payment of ${formatCurrency(charge.outstandingAmount)}`}
          >
            <div className="flex items-start space-x-3">
              {/* Selection indicator */}
              <div className="flex items-center mt-1">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                  isSelected 
                    ? 'bg-blue-600 border-blue-600' 
                    : 'border-gray-300 hover:border-blue-400'
                }`}>
                  {isSelected && (
                    <span className="text-white text-xs font-bold">✓</span>
                  )}
                </div>
              </div>
              
              {/* Main content area */}
              <div className="flex-1 min-w-0">
                {/* Header with title and amount */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <span className="text-lg">{getCategoryIcon(charge.chargeCategory)}</span>
                    <h4 className={`font-medium transition-colors truncate ${
                      isSelected ? 'text-blue-900' : 'text-gray-900'
                    }`}>
                      {charge.chargeName}
                    </h4>
                  </div>
                  
                  {/* Amount - fixed width to prevent cutoff */}
                  <div className="text-right ml-3 flex-shrink-0">
                    <p className={`font-semibold text-lg transition-colors ${
                      isSelected ? 'text-blue-900' : 'text-gray-900'
                    }`}>
                      {formatCurrency(charge.outstandingAmount)}
                    </p>
                    {charge.paidAmount > 0 && (
                      <p className="text-xs text-green-600">
                        Paid: {formatCurrency(charge.paidAmount)}
                      </p>
                    )}
                    {charge.originalAmount !== charge.outstandingAmount && !charge.paidAmount && (
                      <p className="text-xs text-gray-500 line-through">
                        {formatCurrency(charge.originalAmount)}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Status badges */}
                <div className="flex items-center space-x-2 mb-2">
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Outstanding
                  </span>
                  {isSelected && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Selected
                    </span>
                  )}
                </div>
                
                {/* Bill details */}
                <p className="text-sm text-gray-600 mb-1">
                  Bill No: {charge.billNumber} • Bill Date: {formatDate(charge.billDate)}
                </p>
                
                <p className="text-sm text-gray-500">
                  Category: {charge.chargeCategory}
                </p>
              </div>
            </div>
            
            {isSelected && (
              <div className="mt-3 p-3 bg-blue-100 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center mr-2">
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                    <span className="text-sm font-medium text-blue-800">
                      Ready for payment
                    </span>
                  </div>
                  <span className="text-xs text-blue-600 font-medium">
                    Tap "Proceed to Payment" below
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};